// src/pages/AuthPage.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Eye, EyeOff, Info, MailCheck } from "lucide-react";
import LapstrTextLogo from "@/assets/Lapstr-text.png";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { useHaptics } from "@/haptics/HapticsProvider";
import { isNativeApp } from "@/lib/runtime";
import { signInWithNativeGoogle } from "@/lib/nativeGoogleAuth";
import PasswordRequirements from "@/components/PasswordRequirements";
import { isPasswordValid, PASSWORD_POLICY_MESSAGE } from "@/lib/passwordPolicy";

function GoogleIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.702-1.567 2.684-3.874 2.684-6.614Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.836.859-3.047.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.963 10.706A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.706V4.962H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.038l3.007-2.332Z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .956 4.962l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z" />
    </svg>
  );
}

/* Liten återanvändbar PasswordInput. Kan flyttas till components/ui/password-input.jsx. */
function PasswordInput({ value, onChange, id, placeholder = "Lösenord", autoComplete, ...props }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        {...props}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute right-1 top-1/2 -translate-y-1/2 px-2"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Dölj lösenord" : "Visa lösenord"}
      >
        {visible ? (
          <EyeOff className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Eye className="h-4 w-4 text-muted-foreground" />
        )}
      </Button>
    </div>
  );
}

export default function AuthPage() {
  const { user, login, oauthLogin, verify2FA, register, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const native = isNativeApp();

  const [mode, setMode] = useState(() => location.state?.authMode === "register" ? "register" : "login");
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    email: "",
    password: "",
  });
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const googleButtonRef = useRef(null);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [deviceLabel, setDeviceLabel] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const h = useHaptics();
  const prevLenRef = useRef(0);

  const nextDest = useCallback((u) => {
    const role = u?.role || "user";
    return role === "super_admin" || role === "club_admin" ? "/admin" : "/";
  }, []);

  useEffect(() => {
    const note = sessionStorage.getItem("auth_notice");
    if (note) {
      toast.message("Utloggad", { description: note });
      sessionStorage.removeItem("auth_notice");
    }
  }, []);

  useEffect(() => {
    if (user) {
      navigate(nextDest(user), { replace: true });
    }
  }, [user, navigate, nextDest, location]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(loginForm.email, loginForm.password);

      if (res?.twoFactorRequired) {
        setNeeds2FA(true);
        setTicket(res.ticket);
        setDeviceLabel(res.deviceLabel || "Ny enhet");
        toast.message("Tvåstegsverifiering", {
          description: `Ange 6-siffrig kod för ${res.deviceLabel || "ny enhet"}.`,
        });
        return;
      }

      if (res?.ok && res?.user) {
        navigate(nextDest(res.user), { replace: true });
        return;
      }

      throw new Error("Oväntat svar från servern vid inloggning.");
    } catch (err) {
      if (err?.code === "pending_email_verification_required") {
        setRegistrationSuccess({
          email: loginForm.email,
          message:
            err?.message ||
            "Din nya e-postadress är inte bekräftad ännu. Kontrollera din inkorg och bekräfta länken innan du loggar in med den nya adressen.",
        });
        setResendCooldown(0);
        toast.message("Bekräfta din nya e-postadress", {
          description: "Du kan skicka en ny bekräftelselänk härifrån om det behövs.",
        });
        return;
      }
      toast.error("Inloggning misslyckades", {
        description: err?.message || "Något gick fel. Försök igen.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = useCallback(
    async (provider, idToken) => {
      setLoading(true);
      try {
        const res = await oauthLogin(provider, idToken);

        if (res?.twoFactorRequired) {
          setNeeds2FA(true);
          setTicket(res.ticket);
          setDeviceLabel(res.deviceLabel || "Ny enhet");
          toast.message("Tvåstegsverifiering", {
            description: `Ange 6-siffrig kod för ${res.deviceLabel || "ny enhet"}.`,
          });
          return;
        }

        if (res?.ok && res?.user) {
          navigate(nextDest(res.user), { replace: true });
          return;
        }

        throw new Error("Oväntat svar från servern vid OAuth-inloggning.");
      } catch (err) {
        toast.error("Inloggning misslyckades", {
          description: err?.message || "Något gick fel. Försök igen.",
        });
      } finally {
        setLoading(false);
      }
    },
    [oauthLogin, navigate, nextDest]
  );

  const handleNativeGoogleLogin = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const idToken = await signInWithNativeGoogle();
      await handleOAuth("google", idToken);
    } catch (error) {
      if (error?.code === "USER_CANCELLED") return;
      toast.error("Google-inloggning misslyckades", {
        description: error?.message || "Kontrollera Google-konfigurationen och försök igen.",
      });
    } finally {
      setLoading(false);
    }
  };

  const doVerify2FA = useCallback(async () => {
    const sanitized = (code || "").replace(/\D/g, "");
    if (!ticket || sanitized.length !== 6) return;

    setVerifying(true);
    try {
      const res = await verify2FA(ticket, sanitized);
      h.success();
      toast.success("Verifierad - inloggad");
      navigate(nextDest(res?.user), { replace: true });
    } catch (err) {
      h.error();
      toast.error("Felaktig kod", { description: err?.message || "Prova igen." });
      setCode("");
      prevLenRef.current = 0;
    } finally {
      setVerifying(false);
    }
  }, [code, ticket, verify2FA, h, navigate, nextDest]);

  const doRegister = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(registerForm.password)) {
      toast.error("Lösenordet uppfyller inte kraven", { description: PASSWORD_POLICY_MESSAGE });
      return;
    }
    setLoading(true);
    try {
      const res = await register(registerForm.email, registerForm.password);
      setMode("login");
      setRegistrationSuccess({
        email: registerForm.email,
        message: res?.message || "Vi har skickat en bekräftelselänk till din e-postadress.",
      });
      setResendCooldown(60);
      setLoginForm((prev) => ({ ...prev, email: registerForm.email }));
      setRegisterForm({ email: "", password: "" });
    } catch (err) {
      if (err?.code === "email_unverified") {
        setMode("login");
        setRegistrationSuccess({
          email: registerForm.email,
          message:
            err?.message ||
            "Det finns redan ett konto med den här e-postadressen som väntar på bekräftelse. Kontrollera din inkorg eller skicka en ny bekräftelselänk.",
        });
        setResendCooldown(0);
        setLoginForm((prev) => ({ ...prev, email: registerForm.email }));
        setRegisterForm((prev) => ({ ...prev, password: "" }));
        toast.message("Kontot väntar på bekräftelse", {
          description: "Du kan skicka en ny verifieringslänk härifrån.",
        });
        return;
      }
      const description =
        err?.status === 422
          ? "Felaktig e-postadress. Kontrollera att adressen är korrekt."
          : err?.message || "Något gick fel. Försök igen.";

      toast.error("Registrering misslyckades", { description });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (native || !googleClientId || needs2FA || mode !== "login") return;
    const scriptId = "google-oauth-script";

    const renderButton = () => {
      if (!window.google || !googleButtonRef.current) return;
      googleButtonRef.current.innerHTML = "";
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        locale: "sv",
        use_fedcm: false,
        callback: (resp) => {
          if (resp?.credential) handleOAuth("google", resp.credential);
        },
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        width: googleButtonRef.current.offsetWidth || 320,
      });
    };

    if (document.getElementById(scriptId)) {
      requestAnimationFrame(renderButton);
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = renderButton;
    script.onerror = () => {
      toast.error("Google-inloggning kunde inte laddas", {
        description: "Försök igen om en liten stund.",
      });
    };
    document.body.appendChild(script);
  }, [native, googleClientId, needs2FA, mode, handleOAuth]);

  useEffect(() => {
    const sanitized = (code || "").replace(/\D/g, "");
    const prevLen = prevLenRef.current;
    if (sanitized.length === 6 && prevLen !== 6) {
      doVerify2FA();
    }
    prevLenRef.current = sanitized.length;
  }, [code, doVerify2FA]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    const email = registrationSuccess?.email?.trim();
    if (!email || resendLoading || resendCooldown > 0) return;

    setResendLoading(true);
    try {
      const res = await resendVerificationEmail(email);
      toast.success("Verifieringsmejl skickat", {
        description: res?.message || "Om adressen finns skickar vi ett nytt mejl.",
      });
      setResendCooldown(60);
    } catch (err) {
      const retryAfter = Number.parseInt(String(err?.retryAfter ?? ""), 10);
      if (Number.isFinite(retryAfter) && retryAfter > 0) {
        setResendCooldown(retryAfter);
      }
      toast.error("Kunde inte skicka nytt mejl", {
        description: err?.message || "Försök igen om en liten stund.",
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={native ? "native-v3-auth-page" : "min-h-screen flex flex-col items-center justify-center p-6"}>
      <div className={native ? "native-v3-auth-brand" : "mx-auto mb-3 w-[min(92vw,560px)] sm:w-[min(90vw,620px)] md:w-[min(80vw,760px)]"}>
        {native ? (
          <img src="/icons/icon-512x512.png" alt="" aria-hidden="true" className="native-v3-auth-brand-mark" />
        ) : null}
        <img
          src={LapstrTextLogo}
          alt="Lapstr"
          className={native ? "native-v3-auth-brand-wordmark" : "w-[clamp(160px,50vw,480px)] h-auto mx-auto"}
        />
      </div>

      <div className={native ? "native-v3-auth-panel" : "w-full max-w-sm bg-background shadow rounded-xl p-6"}>
        {native ? (
          <p className="native-v3-auth-kicker">
            {needs2FA
              ? "SÄKER INLOGGNING"
              : registrationSuccess
                ? "KONTROLLERA INKORGEN"
                : mode === "register"
                  ? "KOM IGÅNG"
                  : "VÄLKOMMEN TILL LAPSTR"}
          </p>
        ) : null}
        <h1 className={native ? "native-v3-auth-title" : "text-xl font-semibold mb-2 text-center"}>
          {native
            ? needs2FA
              ? "VERIFIERA INLOGGNING"
              : registrationSuccess
                ? "VERIFIERA DIN E-POST"
                : mode === "register"
                  ? "SKAPA KONTO"
                  : "LOGGA IN"
            : "Välkommen"}
        </h1>
        <p className={native ? "native-v3-auth-lead" : "text-sm text-gray-600 text-center mb-4"}>
          {needs2FA
            ? "Ange engångskod från din autentiseringsapp"
            : registrationSuccess
              ? "Nästa steg är att bekräfta din e-postadress"
              : mode === "register"
                ? "Skapa ditt konto och börja använda Lapstr."
                : "Logga in för att använda Lapstr."}
        </p>
        {!needs2FA ? (
          <div className={native ? "native-v3-auth-about-link" : "mb-4 flex justify-center"}>
            <Link
              to="/om-lapstr"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <Info className="h-3.5 w-3.5" />
              Om Lapstr
            </Link>
          </div>
        ) : null}

        {!needs2FA && registrationSuccess ? (
          <div className={native ? "native-v3-auth-verification" : "space-y-5"}>
            <div className={native ? "native-v3-auth-verification-status" : "rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-center"}>
              <div className={native ? "native-v3-auth-status-icon" : "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"}>
                <MailCheck className="h-6 w-6" />
              </div>
              <h2 className={native ? "native-v3-auth-status-title" : "text-lg font-semibold text-emerald-900"}>Kontrollera din e-post</h2>
              <p className={native ? "native-v3-auth-status-copy" : "mt-2 text-sm leading-6 text-emerald-900/90"}>{registrationSuccess.message}</p>
              <p className={native ? "native-v3-auth-status-email" : "mt-3 text-sm font-medium text-emerald-900"}>{registrationSuccess.email}</p>
            </div>

            <div className={native ? "native-v3-auth-steps" : "space-y-2 text-sm text-muted-foreground"}>
              <p>Klicka på länken i mejlet för att aktivera ditt konto.</p>
              <p>Om du inte hittar mejlet, kontrollera även skräppost eller kampanjer.</p>
            </div>

            <div className={native ? "native-v3-auth-actions" : "space-y-3"}>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleResendVerification}
                disabled={resendLoading || resendCooldown > 0}
              >
                {resendLoading
                  ? "Skickar nytt mejl..."
                  : resendCooldown > 0
                    ? `Skicka om länk om ${resendCooldown}s`
                    : "Skicka om länken"}
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  setRegistrationSuccess(null);
                  setResendCooldown(0);
                  setMode("login");
                }}
              >
                Till inloggning
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setRegistrationSuccess(null);
                  setResendCooldown(0);
                  setMode("register");
                }}
              >
                Använd en annan e-postadress
              </Button>
            </div>
          </div>
        ) : !needs2FA ? (
          <Tabs value={mode} onValueChange={setMode} className={native ? "native-v3-auth-tabs" : undefined}>
            <TabsList className={native ? "native-v3-auth-tab-list" : "grid w-full grid-cols-2"}>
              <TabsTrigger value="login">Logga in</TabsTrigger>
              <TabsTrigger value="register">Registrera</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className={native ? "native-v3-auth-tab-content" : "mt-4 min-h-[320px]"}>
              <form onSubmit={doLogin} className={native ? "native-v3-auth-form" : "space-y-4"}>
                <div>
                  <Label htmlFor="login-email">E-post</Label>
                  <Input
                    id="login-email"
                    placeholder="Din@epost.se"
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    autoComplete="email"
                    className="placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Lösenord</Label>
                  <PasswordInput
                    id="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    autoComplete="current-password"
                    className="placeholder:text-muted-foreground"
                  />
                </div>
                <div className="text-right">
                  <Link to="/auth/forgot-password" className="text-sm font-medium text-primary hover:underline">
                    Glömt lösenord?
                  </Link>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Loggar in..." : "Logga in"}
                </Button>
              </form>

              {googleClientId ? (
                <div className="mt-4">
                  <div className="relative my-4">
                    <div className="h-px bg-border" />
                    <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-background px-2 text-xs text-muted-foreground">
                      eller
                    </span>
                  </div>
                  <div className="space-y-2">
                    {native ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2 bg-background"
                        onClick={handleNativeGoogleLogin}
                        disabled={loading}
                      >
                        <GoogleIcon />
                        {loading ? "Öppnar Google..." : "Fortsätt med Google"}
                      </Button>
                    ) : (
                      <div ref={googleButtonRef} className="w-full" />
                    )}
                  </div>
                </div>
              ) : null}
            </TabsContent>

            <TabsContent value="register" className={native ? "native-v3-auth-tab-content" : "mt-4 min-h-[320px]"}>
              <form onSubmit={doRegister} className={native ? "native-v3-auth-form" : "space-y-4"}>
                <div>
                  <Label htmlFor="email">E-post</Label>
                  <Input
                    id="email"
                    placeholder="E-mail"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    autoComplete="email"
                    className="placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password">Lösenord</Label>
                  <PasswordInput
                    id="reg-password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    autoComplete="new-password"
                    className="placeholder:text-muted-foreground"
                  />
                  <PasswordRequirements password={registerForm.password} />
                </div>
                <Button type="submit" className="w-full" disabled={loading || !isPasswordValid(registerForm.password)}>
                  {loading ? "Skapar konto..." : "Skapa konto"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        ) : (
          <form onSubmit={doVerify2FA} className={native ? "native-v3-auth-2fa" : "space-y-4 mt-2"}>
            <div className="space-y-1 text-center text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Tvåstegsverifiering</p>
              <p>Bekräftar inloggning för {deviceLabel || "angiven enhet"}.</p>
            </div>

            <div className="flex justify-center">
              <InputOTP
                value={code}
                onChange={(value) => {
                  const sanitized = (value || "").replace(/\D/g, "");
                  setCode(sanitized);
                }}
                maxLength={6}
                containerClassName="justify-center"
                autoFocus
                disabled={verifying}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="space-y-3">
              <Button type="submit" className="w-full" disabled={verifying || code.length !== 6}>
                {verifying ? "Verifierar..." : "Verifiera"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setNeeds2FA(false);
                  setTicket(null);
                  setDeviceLabel("");
                  setCode("");
                  prevLenRef.current = 0;
                }}
                disabled={verifying}
              >
                Avbryt
              </Button>
            </div>
          </form>
        )}

        {!needs2FA ? (
          <p className={native ? "native-v3-auth-legal" : "mt-5 text-center text-xs leading-relaxed text-muted-foreground"}>
            Läs våra{" "}
            <Link to="/legal#terms" className="font-medium text-foreground underline underline-offset-4">
              användarvillkor
            </Link>{" "}
            och vår{" "}
            <Link to="/legal#privacy" className="font-medium text-foreground underline underline-offset-4">
              integritetspolicy
            </Link>.
          </p>
        ) : null}
      </div>
    </div>
  );
}
